import React, { useState, useEffect, useRef, useMemo } from 'react';
import RecipeCard from './RecipeCard';
import RecipeModal from './RecipeModal';
import { Search, Filter, ChevronDown } from 'lucide-react';
import { useStore, Recipe } from '../../store/useStore';
import AddMealModal from './AddMealModal';
import { ChevronRight } from 'lucide-react';
import classifyCookTime, { classifyCalories } from '@/lib/cookTimeClassifier';


function decodeHtmlEntities(str: string): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(str, "text/html");
  return doc.documentElement.textContent || '';
}

type FilterChipProps = {
  active: boolean;
  onClick: () => void;
  activeClass: string;
  children: React.ReactNode;
};


function FilterChip({
  active,
  onClick,
  activeClass,
  children,
}: {
  active: boolean;
  onClick: () => void;
  activeClass: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`px-3 py-1.5 rounded-full text-sm ${active
        ? `${activeClass} text-white`
        : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
        }`}
    >
      {children}
    </button>
  );
}

type FilterProps = {
  categories: string[];
  tags: string[];
  difficulties: string[];
  spiceLevel: string[];
  cuisine: string[];
  calories: string[];
  cookTimes: string[];
  onApply: (filters: Filter) => void;
  reset?: boolean;
  onResetDone?: () => void;
  
}

type Filter = {
  category: string;
  tag: string;
  difficulty: string;
  spiceLevel: string;
  cuisine: string;
  favoritesOnly: boolean;
  calories: string;
  cookTime: string;
}

function FilterModal({
  categories = [],
  tags = [],
  difficulties = [],
  spiceLevel = [],
  cuisine = [],
  calories = [],
  reset = false,
  cookTimes = [],
  onApply = (filters: Filter) => {},
  onResetDone = () => {}
}: FilterProps) {
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>("All");
  const [selectedTag, setSelectedTag] = useState<string>("All");
  const [selectedDifficulty, setSelectedDifficulty] = useState<string>("All");
  const [selectedSpiceLevel, setSelectedSpiceLevel] = useState<string>("All");
  const [selectedCuisine, setSelectedCuisine] = useState<string>("All");
  const [selectedCalorie, setSelectedCalorie] = useState<string>("All");
  const [selectedCookTime, setSelectedCookTime] = useState<string>("All");
  const [showFavorites, setShowFavorites] = useState(false);
  const [clearFilter, setClearFilter] = useState(reset);

  const handleClear = () => {
    console.log("ckearing...")
    setSelectedCategory("All");
    setSelectedTag("All");
    setSelectedDifficulty("All");
    setSelectedSpiceLevel("All");
    setSelectedCuisine("All");
    setSelectedCalorie("All");
    setSelectedCookTime('All');
    setShowFavorites(false);

    onApply({
      category: "All",
      tag: "All",
      difficulty: "All",
      spiceLevel: "All",
      cuisine: "All",
      favoritesOnly: false,
      calories: "All",
      cookTime: "All"
    });
    setShowFilters(false);
  };

  useEffect(() => {
    if (reset) handleClear();
    onResetDone?.();
  }, [reset]);

  const filterCount =
    (!selectedCategory.toLowerCase().includes("all") ? 1 : 0) +
    (!selectedTag.toLowerCase().includes("all") ? 1 : 0) +
    (!selectedDifficulty.toLowerCase().includes("all") ? 1 : 0) +
    (!selectedSpiceLevel.toLowerCase().includes("all") ? 1 : 0) +
    (!selectedCuisine.toLowerCase().includes("all") ? 1 : 0) +
    (!selectedCalorie.toLowerCase().includes("all") ? 1 : 0) +
    (!selectedCookTime.toLowerCase().includes("all") ? 1 : 0) +
    (showFavorites ? 1 : 0);

  return (
    <div className="relative">
      {/* Trigger */}
      <div data-tooltip="filter-recipe">
        <button
          onClick={() => setShowFilters(true)}
          className="flex justify-center items-center px-4 py-2 bg-secondary-700 text-white rounded-md hover:bg-secondary/90"
        >
          <Filter size={18} className="mr-2" />
          Filters {filterCount > 0 ? `(${filterCount})` : ""}
          {/* {showFilters && <ChevronDown
            width={18}
            height={18}
            className={`ml-2 transition-transform duration-300 ${showFilters ? 'rotate-180' : 'rotate-0'}`} />} */}

        </button>
      </div>

      {showFilters && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setShowFilters(false)}
          />

          {/* Modal */}
          <div className="relative z-10 w-[90%] max-w-2xl bg-white dark:bg-gray-800 rounded-xl p-5 md:p-6 shadow-lg max-h-[85vh] overflow-y-auto">
            {/* --- Two Column Grid --- */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
              {/* Meal Type */}
              <FilterSelect
                label="Meal Type"
                value={selectedCategory}
                options={categories}
                onChange={setSelectedCategory}
              />

              {/* Tag */}
              <FilterSelect
                label="Food Tag"
                value={selectedTag}
                options={tags}
                onChange={setSelectedTag}
              />

              {/* Calories */}
              <FilterSelect
                label="Calories"
                value={selectedCalorie}
                options={calories}
                onChange={setSelectedCalorie}
              />

              {/* Difficulty */}
              <FilterSelect
                label="Skill Level"
                value={selectedDifficulty}
                options={difficulties}
                onChange={setSelectedDifficulty}
              />

              {/* Cook Time */}
              <FilterSelect
                label="Cook Time"
                value={selectedCookTime}
                options={cookTimes}
                onChange={setSelectedCookTime}
              />

              {/* Spice Level */}
              <FilterSelect
                label="Spice Level"
                value={selectedSpiceLevel}
                options={spiceLevel}
                onChange={setSelectedSpiceLevel}
              />

              {/* Cuisine */}
              <FilterSelect
                label="Cuisine"
                value={selectedCuisine}
                options={cuisine}
                onChange={setSelectedCuisine}
              />

              {/* Favorites Toggle */}
              <div className="flex flex-col justify-center">
                <label
                  htmlFor="fav"
                  className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  Favourites Only
                </label>
                <button
                  type="button"
                  id="fav"
                  aria-pressed={showFavorites}
                  onClick={() => setShowFavorites((prev) => !prev)}
                  className={`relative w-12 h-6 rounded-full transition-colors ${showFavorites ? "bg-secondary-700" : "bg-gray-300 dark:bg-gray-600"
                    }`}
                >
                  <span
                    className={`absolute left-1 top-1 h-4 w-4 rounded-full bg-white transition-transform ${showFavorites ? "translate-x-6" : ""
                      }`}
                  />
                </button>
              </div>
            </div>

            {/* Actions */}
            <div className="mt-6 flex justify-end gap-3 border-t border-gray-200 dark:border-gray-700 pt-4">
              <button
                onClick={handleClear}
                className="px-4 py-2 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 text-sm"
              >
                Clear
              </button>
              <button
                onClick={() => {
                  // send filters back to parent
                  if (onApply) {
                    onApply({
                      category: selectedCategory,
                      tag: selectedTag,
                      difficulty: selectedDifficulty,
                      spiceLevel: selectedSpiceLevel,
                      cuisine: selectedCuisine,
                      favoritesOnly: showFavorites,
                      calories: selectedCalorie,
                      cookTime: selectedCookTime
                    });
                  }
                  setShowFilters(false);
                }}
                className="px-4 py-2 rounded-md bg-secondary-700 text-white hover:bg-secondary/90 text-sm"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* --- Small reusable select component --- */
function FilterSelect({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        {label}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-md border-gray-300 dark:border-gray-700 dark:bg-gray-700 dark:text-gray-100 px-2 py-1.5"
      >
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </div>
  );
}



const Cookbook: React.FC = () => {
  const user = useStore((state) => state.user);
  const recipes = useStore((state) => state.recipes);
  const toggleFavorite = useStore((state) => state.toggleFavorite);
  const subscription = useStore((state) => state.subscription);
  const addRecipe = useStore((state) => state.addRecipe);
  const addNotification = useStore((state) => state.addNotification);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedTag, setSelectedTag] = useState('All');
  const [selectedDifficulty, setSelectedDifficulty] = useState('All');
  const [selectedCuisine, setSelectedCuisine] = useState('All');
  const [selectedCookTime, setSelectedCookTime] = useState('All');
  const [selectedCalories, setSelectedCalories] = useState('All');
  const [selectedSpiceLevel, setSelectedSpiceLevel] = useState('All');
  const [showFavorites, setShowFavorites] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedRecipe, setSelectedRecipe] = useState<Recipe | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [categories, setCategories] = useState(['All', 'Breakfast', 'Main Course', 'Salad', 'Dessert', 'Snack']);
  const [tags, setTags] = useState(['All']);
  const [cuisine, setCusines] = useState(['All']);
  const [difficulties, setDifficulties] = useState(['All', 'Easy', 'Medium', 'Hard']);
  const [spiceLevel, setSpiceLevel] = useState(['All', 'Mild', 'Medium', 'Hot']);
  const [cookTime, setCookTime] = useState(['All', 'Quick', 'Medium', 'Leisurely']);
  const [clearFilters, setClearFilters] = useState(false);
  const [calories, setCalories] = useState(['All', 'Light', 'Balanced', 'Hearty']);
  const [theme, setTheme] = useStore((state) => [
    state.userPreferences?.theme || 'dark',
    state.updatePreferences
  ]);

  // Theme handling
  useEffect(() => {
    if (!theme) {
      const newTheme = localStorage.getItem('theme') || 'dark';
      if (newTheme === 'light' || newTheme === 'dark') {
        setTheme({ theme: newTheme });
      }
    }
  }, [theme]);

  const showFiltersRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickAnywhere = async (event: MouseEvent) => {
      // console.log('Clicked somewhere on the page:', event);
      if (showFiltersRef.current && !showFiltersRef.current.contains(event.target as Node)) {
        setShowFilters(false);
      }
    };

    let timeoutId: number;

    if (showFilters) {
      // Delay to avoid closing immediately after opening
      timeoutId = window.setTimeout(() => {
        window.addEventListener('click', handleClickAnywhere);
      }, 0);
    }

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('click', handleClickAnywhere);
    };
  }, [showFilters]);

  useEffect(() => {
    try {
      setIsLoading(true);
      let fetchedRecipes = recipes;

      const uniqueCategories = [
        ...new Set(fetchedRecipes.flatMap(recipe => recipe?.category || []))
      ];
      const allowedDifficulties = ["Easy", "Medium", "Hard"];

      const uniqueDifficulties = [
        ...new Set(
          fetchedRecipes
            .map(recipe => recipe?.difficulty)
            .filter(difficulty => allowedDifficulties.includes(difficulty))
        )
      ];

      const spiceLevel = [
        ...new Set(fetchedRecipes.flatMap(recipe => recipe?.spiceLevel || []))
      ];

      const uniqueTags = [
        ...new Set(fetchedRecipes.flatMap(recipe => recipe?.tags || []))
      ];

      const uniqueCalories = [
        ...new Set(
          fetchedRecipes
            .flatMap(recipe => classifyCalories(Number(recipe?.calories)))
        )
      ];

      const uniqueCookTimes = [
        ...new Set(
          fetchedRecipes
            .flatMap(recipe => classifyCookTime(recipe?.cookTime))
        )
      ];


      const mealTypes = ["Breakfast", "Lunch", "Dinner"];
      let meals = [];
      let cuisines = [];
      let cleanTags = [];

      for (const item of uniqueCategories) {
        const cleanCategory = decodeHtmlEntities(item);
        if (mealTypes.includes(item)) {
          meals.push(cleanCategory);
        } else {
          cuisines.push(cleanCategory);
        }
      }

      for (const item of uniqueTags) {
        const cleanTag = decodeHtmlEntities(item);
        cleanTags.push(cleanTag);
      }

      // console.log('Unique Categories:', uniqueCategories);
      // console.log('Unique Difficulties:', uniqueDifficulties);
      // Set categories and difficulties for filtering

      setCategories(['All', ...meals]);
      setTags(['All', ...cleanTags]);
      setDifficulties(['All', ...uniqueDifficulties]);
      setCusines(['All', ...cuisines]);
      setSpiceLevel(['All', ...spiceLevel]);
      setCalories(['All', ...uniqueCalories]);
      setCookTime(['All', ...uniqueCookTimes]);

      useStore.setState({ recipes: fetchedRecipes });
    } catch (error) {
      console.error('Error fetching recipes:', error);
      addNotification({
        title: 'Error',
        message: 'Failed to load recipes. Please try again.',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  const filteredRecipes = useMemo(() => {
    return recipes.filter((recipe) => {
      // Only show custom recipes belonging to current user
      if (recipe.userId && recipe.userId !== user?.id) return false;

      // Block variations if not subscribed
      if (!subscription && recipe.isVariation) return false;

      const matchesSearch = recipe.title
        .toLowerCase()
        .includes(searchTerm.toLowerCase());

      const matchesCategory =
        selectedCategory === "All" || recipe.category.includes(selectedCategory);

      const matchesTag =
        selectedTag === "All" || recipe.tags?.includes(selectedTag);

      const matchesDifficulty =
        selectedDifficulty === "All" || recipe.difficulty === selectedDifficulty;

      const matchesFavorites = !showFavorites || recipe.isFavorite;

      const matchesCuisine =
        selectedCuisine === "All" || recipe.category.includes(selectedCuisine);

      const matchesCalories =
        selectedCalories === "All" || classifyCalories(Number(recipe.calories)) === selectedCalories;

      const matchesCookTime =
        selectedCookTime === "All" || classifyCookTime(recipe.cookTime) === selectedCookTime;

      const matchesSpiceLevel =
        selectedSpiceLevel === "All" ||
        recipe.spiceLevel?.includes(selectedSpiceLevel);

      return (
        matchesSearch &&
        matchesCategory &&
        matchesTag &&
        matchesDifficulty &&
        matchesFavorites &&
        matchesCuisine &&
        matchesSpiceLevel &&
        matchesCalories &&
        matchesCookTime
      );
    });
  }, [
    recipes,
    user?.id,
    subscription,
    searchTerm,
    selectedCategory,
    selectedTag,
    selectedDifficulty,
    showFavorites,
    selectedCuisine,
    selectedSpiceLevel,
    selectedCalories,
    selectedCookTime
  ]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-secondary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Recipe Collection ({Math.round(recipes.length / 100) * 100}+)</h1>

          <div className="flex gap-3">
            <FilterModal
              categories={categories}
              tags={tags}
              difficulties={difficulties}
              spiceLevel={spiceLevel}
              cuisine={cuisine}
              calories={calories}
              cookTimes={cookTime}
              reset={clearFilters}
              onApply={(filters) => {
                // console.log("Show Favorites", filters)
                setSelectedCategory(filters.category);
                setSelectedTag(filters.tag);
                setSelectedCuisine(filters.cuisine);
                setSelectedSpiceLevel(filters.spiceLevel);
                setShowFavorites(filters.favoritesOnly);
                setSelectedDifficulty(filters.difficulty);
                setSelectedCalories(filters.calories);
                setSelectedCookTime(filters.cookTime);
              }}
              onResetDone={() => setClearFilters(false)}
            />
            {/* <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <Filter size={18} className="mr-2" />
              Filters
              {showFilters && <ChevronDown
                width={18}
                height={18}
                className={`ml-2 transition-transform duration-300 ${showFilters ? 'rotate-180' : 'rotate-0'}`} />}
            </button> */}
          </div>

        </div>

        <div className="relative max-w-full mt-4">
          <button
            onClick={() => window.open('https://ezyvibes.com/disclaimer', '_blank')}
            className="flex items-center justify-between w-full bg-yellow-100 hover:bg-yellow-200 text-yellow-900 px-4 py-2 rounded-lg mb-4 transition-colors duration-300 cursor-pointer focus:outline-none focus:ring-2 focus:ring-yellow-500"
          >
            <span className="truncate font-medium">
              ⚠️ Allergy Notice:
              <span className="hidden sm:inline">
                &nbsp;Ezyvibes recipes may contain common allergens. Please verify all ingredients and labels before use.
              </span>
            </span>
            <span className="flex items-center gap-1 text-sm text-yellow-800 group-hover:text-yellow-900 transition-colors">
              Learn More <ChevronRight className="w-4 h-4" />
            </span>
          </button>
        </div>


        <div className="relative" data-tooltip="search-recipe">
          <input
            type="text"
            placeholder="Search recipes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full py-3 px-4 pl-10 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary focus:border-secondary bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
          />
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
        </div>
        <div>
          <span className='text-sm'>Showing {filteredRecipes.length} recipes</span>
        </div>

        {/* {!subscription && (
        <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4 text-orange-800 dark:text-orange-300">
          <p className="text-sm">
            Upgrade to Pro to create your own recipe variations and access all features!
          </p>
        </div>
      )} */}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredRecipes.map((recipe, index) => (
            <RecipeCard
              key={recipe.id}
              recipe={recipe}
              i={index}
              onToggleFavorite={toggleFavorite}
              onClick={(recipe) => setSelectedRecipe(recipe)}
            />
          ))}
        </div>

        {filteredRecipes.length === 0 && !isLoading && (
          <div className="text-center py-10">
            <p className="text-gray-500 dark:text-gray-400">
              {recipes.length === 0
                ? "You haven't created any recipes yet. Click 'Create Recipe' to get started!"
                : "No recipes found matching your criteria."
              }
            </p>
            {recipes.length > 0 && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSelectedCategory('All');
                  setSelectedTag('All');
                  setSelectedDifficulty('All');
                  setSelectedCookTime('All');
                  setShowFavorites(false);
                  setSelectedCalories('All');
                  setSelectedCuisine('All');
                  setSelectedSpiceLevel('All');
                  setClearFilters(true);
                }}
                className="mt-3 text-secondary hover:text-secondary/80"
              >
                Clear all filters
              </button>
            )}
          </div>
        )}
      </div>

      {selectedRecipe && (
        <RecipeModal
          recipe={selectedRecipe}
          onClose={() => setSelectedRecipe(null)}
        />
      )}

      <AddMealModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onAddMeal={(recipe: Recipe) => {
          addRecipe(recipe);
          setShowCreateModal(false);
          addNotification({
            title: 'Recipe Created',
            message: `${recipe.title} has been added to your cookbook`,
            type: 'success'
          });
        }}
      />
    </div>
  );
};

export default Cookbook;